import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  AppCard,
  AppDeleteModal,
  AppModal,
  AppNoInfo,
  AppRemixIcon,
} from '@/layout/AppComponents';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import styled from "styled-components";
import { axiosPath } from '../../../routing/helpers/router';
import BreadcrumbHandler from "../../../layout/components/breadcrumb/BreadcrumbHandler";
import useIsMounted from '../../../layout/components/hooks/useIsMounted';
import * as currentPageAction from '../../../store/actions/currentPageAction';
import errorHandler from '@/layout/components/error/ErrorHandler';
import colors from '../../../constants/colors';
import PendingItemListPublicationInfo from '../../../layout/screenSpecifics/Modules/DocumentManager/PendingItemListPublicationInfo';
import PendingItemListPublicationHome from '../../../layout/screenSpecifics/Modules/DocumentManager/PendingItemListPublicationHome';
import DocumentManagerSidebar from '../../../layout/screenSpecifics/Modules/DocumentManager/Sidebar/DocumentManagerSidebar';
import * as moduleAction from '../../../store/actions/moduleAction';

export default function ModuleDocumentManagerPendingItemListScreen() {
  const { moduleBridgeHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const moduleData = useSelector(
    (s) => s?.module?.[moduleBridgeHex]?.moduleData
  );
  const permissionsLevel = useSelector(
    (s) => s.module[moduleBridgeHex]?.userBridge?.permissions?.level
  );
  const loading = useSelector((state) => state.currentPage?.loading);
  const pendingItems = useSelector((state) => state.currentPage?.pendingItems);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const reloadModuleData = useSelector(
    (state) => state.currentPage?.reloadModuleData
  );

  useEffect(() => {
    dispatch(currentPageAction.change('loading', true));
    return () => {
      dispatch(currentPageAction.empty());
    };
  }, []);

  // > LOAD PENDING ITEMS
  const loadPendingItems = async () =>
      axios
        .post(axiosPath("api_r_user_module_documentManager_pendingItem_list"), {
          moduleBridgeHex,
          isAdmin: permissionsLevel === 0,
        })
        .then(({ data }) => {
          if (isMounted.current) {
            dispatch(currentPageAction.change("pendingItems", data, true));
          }
        })
        .catch((e) => dispatch(errorHandler(e)))
        .finally(() =>
          dispatch(currentPageAction.change("loading", false, true))
        )

  useEffect(() => {
    if (loading && permissionsLevel !== undefined) loadPendingItems();
  }, [loading, permissionsLevel]);
  // < LOAD PENDING ITEMS

  const handleDelete = useCallback(
    async () =>
      axios
        .post(
          axiosPath(
            "api_r_user_module_documentManager_item_publication_delete"
          ),
          { moduleBridgeHex, itemHex: showDeleteModal }
        )
        .then(() => {
          if (isMounted.current) {
            dispatch(currentPageAction.change("loading", true, true));
            setShowDeleteModal(false);
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, showDeleteModal]
  );

    // > LOAD MODULE DATA //
    const loadModuleData = useCallback(async () => {
      dispatch(
        moduleAction.fetch(
          axiosPath("api_r_user_module_documentManager_getModuleData"),
          "moduleData",
          {
            moduleBridgeHex,
          }
        )
      );
    }, []);
  
    useEffect(() => {
      loadModuleData();
    }, [reloadModuleData]);
    // > LOAD MODULE DATA //
  
    // < STORE CALLS
    const loadModulePermissions = useCallback(async () => {
      try {
        await dispatch(
          moduleAction.fetch(
            axiosPath("api_r_user_module_documentManager_getPermissions"),
            "permissions",
            {
              moduleBridgeHex,
              subType: "permissions",
            }
          )
        );
        await dispatch(
          moduleAction.fetch(
            axiosPath("api_r_user_module_documentManager_getPermissions"),
            "userBridge",
            {
              moduleBridgeHex,
              subType: "userBridge",
            }
          )
        );
      } catch (err) {}
    }, [moduleBridgeHex]);
  
    useEffect(() => {
      loadModulePermissions();
    }, [moduleBridgeHex]);
    // < STORE CALLS

  return (
    <>
      <DocumentManagerSidebar activeRoute="user_module_documentManager_pendingItem_list" />
      <BreadcrumbHandler
        paths={
          moduleData
            ? [
                {
                  name: `Repositorio ${
                    permissionsLevel === 0 ? '(ADMINISTRADOR)' : ''
                  }`,
                  route: {
                    name: 'user_module_documentManager_item_list',
                    params: {
                      moduleBridgeHex,
                      parentItemHex: '0',
                      page: '1',
                      filtersHex: '0',
                    },
                  },
                },
                {
                  name: 'Publicaciones pendientes de validar',
                },
              ]
            : [{ name: '...' }]
        }
      />
      <main role="main" className="col-md-12 col-lg-12 px-md-4 mb-5">
        <div className="justify-content-between flex-wrap flex-md-nowrap align-items-center pt-0 pb-0 mb-0">
          <AppCard header="Publicaciones pendientes de validar">
            <div className="mt-3">
              {loading ? (
                <AppNoInfo iconSize={64} minHeight={window.innerHeight - 400} />
              ) : pendingItems?.length > 0 ? (
                <>
                  <TableHeader>
                    <div>Nombre</div>
                    <div>Pendiente de validar desde</div>
                    <div>Rechazada desde</div>
                    <div />
                  </TableHeader>
                  {pendingItems?.map((pi) => (
                    <React.Fragment key={pi?.hex}>
                      <Container
                        key={pi?.hex}
                        className="p-2"
                        onDoubleClick={() => setShowInfoModal(pi?.hex)}
                        $isRejected={pi?.validationRejectObservations !== null}
                      >
                        <div className="col-title">
                          <span>{pi?.title}</span>
                        </div>
                        <div>{pi?.pendingValidationDate}</div>
                        <div>{pi?.rejectedDate}</div>
                        <div className="col-options">
                          <div className="options">
                            <span
                              className="pointer pl-2 pr-2"
                              title="Eliminar"
                              onClick={() => setShowDeleteModal(pi?.hex)}
                            >
                              <AppRemixIcon
                                icon="delete-bin-6"
                                color={colors.danger}
                              />
                            </span>
                          </div>
                        </div>
                      </Container>
                      {permissionsLevel === 0 ? (
                        <AppModal
                          show={showInfoModal === pi?.hex}
                          title="Validación documento"
                          size="xl"
                          onClose={() => setShowInfoModal(false)}
                        >
                          <PendingItemListPublicationInfo
                            element={pi}
                            onClose={() => setShowInfoModal(false)}
                          />
                        </AppModal>
                      ) : (
                        <AppModal
                          show={showInfoModal === pi?.hex}
                          title="Información"
                          size="xl"
                          onClose={() => setShowInfoModal(false)}
                        >
                          {pi?.validationRejectObservations !== null ? (
                            <PendingItemListPublicationHome
                              element={pi}
                              onClose={() => setShowInfoModal(false)}
                            />
                          ) : (
                            <PendingItemListPublicationInfo
                              element={pi}
                              onClose={() => setShowInfoModal(false)}
                            />
                          )}
                        </AppModal>
                      )}
                    </React.Fragment>
                  ))}
                  <AppDeleteModal
                    show={showDeleteModal}
                    title="Eliminar publicación"
                    onClose={() => setShowDeleteModal(undefined)}
                    onConfirm={handleDelete}
                  />
                </>
              ) : (
                <div className="alert alert-info">
                  No hay publicaciones pendientes de validar
                </div>
              )}
            </div>
          </AppCard>
        </div>
      </main>
    </>
  );
}

const Container = styled.div`
  border-bottom: solid 1px lightgrey;
  overflow: auto;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 300px;
  background-color: ${(props) => (props.$isRejected ? '#FFD8DB' : '#fff')};

  .col-title {
    cursor: default;
  }
  .col-options {
    display: flex;
    justify-content: end;
    .options {
      display: none;
    }
  }

  &:hover {
    background-color: ${colors.grey200};
    .options {
      display: flex;
    }
  }
`;

const TableHeader = styled.div`
  position: -webkit-sticky;
  position: sticky;
  top: 40px;
  overflow: auto;

  border-bottom: solid 1px lightgrey;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 300px;
  padding-top: 8px;
  padding-bottom: 8px;

  div {
    font-weight: bold;
  }
`;
